import { DeleteForever, Search, ShoppingCart } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Autocomplete,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Theme,
  Tooltip,
  Typography,
  Divider,
  useMediaQuery
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Field } from 'formik';
import { useSnackbar } from 'notistack';
import { FC, useEffect, useMemo, useState } from 'react';
import { Prompt } from 'react-router-dom';
import {
  Button as SMButton,
  EditableCell,
  Loader,
  Table,
  ImageModal,
  SMBSelect
} from '../../../components';
import {
  createOrder,
  getNeighborhoodVendors,
  getPlaceOrderDescriptions,
  getPlaceOrderTypes,
  getReturnParts,
  getUndefinedParts
} from '../../../fetch';
import { formatMoney, UNDEFINED_PART } from '../../../helpers';
import {
  ICreateOrder,
  IDescription,
  INeighborhoodVendor,
  IOrderType,
  IPart,
  IReturnPartPO,
  IReturnPOPart,
  IUndefinedPart
} from '../../../models';
import { PartNameRow } from '../order-management-detail/part-name-row';
import { PriceRow } from '../order-management-detail/price-row';
import { VendorRow } from '../order-management-detail/vendor-row';
import { FindPartsModal } from './find-parts-modal';
import { SpendLimitModal } from './limit-modal';
import { PlaceOrderImages } from './PlaceOrderImages';
import { MobileParts } from '../order-management-detail/mobile-parts';

const CHANGE_ORDER_TYPES = {
  6: true,
  7: true,
  10: true,
  11: true
};
interface IOrderDetails {
  initialValues: any;
  values: any;
  errors: any;
  touched: any;
  handleBlur: any;
  setFieldValue: (key: string, val: any) => void;
  setFieldTouched: (key: string, val: any) => void;
  fetchHistory: (page?: number, rowsPerPage?: number) => void;
  handleOrderTypeChange: (val: string) => void;
  isLoadingHistory: boolean;
  isValid: boolean;
  setParts: (val: IPart[]) => void;
  parts: IPart[];
  isLoadingFavoriteOrder: boolean;
  isReturnPurchaseOrder?: boolean;
}

export const OrderDetails: FC<IOrderDetails> = ({
  isValid,
  initialValues,
  values,
  errors,
  touched,
  handleBlur,
  setFieldValue,
  fetchHistory,
  setFieldTouched,
  handleOrderTypeChange,
  isLoadingHistory,
  parts,
  setParts,
  isLoadingFavoriteOrder,
  isReturnPurchaseOrder
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [showFindPartsModal, setShowFindPartsModal] = useState<boolean>(false);
  const [isLoadingTypes, setLoadingTypes] = useState<boolean>(false);
  const [isSaving, setSaving] = useState<boolean>(false);
  const [orderTypes, setOrderTypes] = useState<IOrderType[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showLimitModal, setShowLimitModal] = useState<boolean>(false);
  const [isLoadingVendors, setIsLoadingVendors] = useState<boolean>(false);
  const [availableVendors, setAvailableVendors] = useState<INeighborhoodVendor[] | null>(null);
  const [selectedImage, setSelectedImage] = useState<string>('');

  const isReturnPOOrderType = isReturnPurchaseOrder || values.orderType === 9;

  const fetchOrderTypes = async () => {
    setLoadingTypes(true);
    try {
      const res = await getPlaceOrderTypes({
        isActive: true
      });
      if (res) {
        setOrderTypes(res);
      } else {
        return enqueueSnackbar('Error loading order types, please try again.', {
          variant: 'error'
        });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Error loading order types, please try again.', {
        variant: 'error'
      });
    }
    setLoadingTypes(false);
  };

  const fetchVendors = async (neighborhoodId, lotId) => {
    setIsLoadingVendors(true);

    try {
      const res = await getNeighborhoodVendors(neighborhoodId, {
        lotNumber: lotId,
        isReturnPO: isReturnPOOrderType
      });
      setAvailableVendors(res.records);
    } catch (error) {
      console.error(error);
    }

    setIsLoadingVendors(false);
  };

  const [isLoadingDescriptions, setLoadingDescriptions] = useState<boolean>(false);
  const [descriptions, setDescriptions] = useState<IDescription[] | null>(null);
  const fetchOrderDescriptions = async () => {
    setLoadingDescriptions(true);
    try {
      const res = await getPlaceOrderDescriptions({
        isActive: true
      });
      if (res) {
        setDescriptions(res);
      } else {
        enqueueSnackbar('Error loading order descriptions, please try again.', {
          variant: 'error'
        });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Error loading order descriptions, please try again.', {
        variant: 'error'
      });
    }
    setLoadingDescriptions(false);
  };

  useEffect(() => {
    fetchOrderTypes();
    fetchOrderDescriptions();
    fetchUndefinedPartOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (values.neighborhoodId && values.lotNumber) {
      fetchVendors(values.neighborhoodId, values.lotNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.neighborhoodId, values.lotNumber, isReturnPOOrderType]);

  const isMobile = useMediaQuery('(max-width: 960px)');

  const handleSubmit = async (orderAnyway?: boolean) => {
    setSaving(true);
    try {
      const data: ICreateOrder = {
        neighborhoodId: Number(values.neighborhoodId),
        lotNumber: values.lotNumber,
        isChangeOrder: values.changeOrder,
        chargeVendors: values.chargeVendors.map(vendor => ({
          vendorId: vendor.id,
          amount: null
        })),
        type: values.orderType ? Number(values.orderType) : null,
        descriptionId: values.descriptionId ? Number(values.descriptionId) : null,
        notes: values.notes,
        parts: parts.map(p => ({
          partId: p.id < 0 ? null : p.id,
          quantity: Number(p.quantity),
          vendorId: p?.vendorId ?? null,
          undefinedPartDescriptionId: p?.undefinedPartDescriptionId ?? null,
          price: values.changeOrder && p?.pricePerUnit ? p?.pricePerUnit : null,
          // will be there if it is a Return PO order type
          purchaseOrderNumber: p?.purchaseOrderNumber || undefined
        })),
        // send along valid guid strings
        photoGuids: values.photoGuids.filter(Boolean),
        unapprovedOrderAlert: orderAnyway || false
      };
      const res = await createOrder(data);

      if (res?.Type === 'Error') {
        if (
          res &&
          res.Detail &&
          (res.Detail.includes('limit') ||
            res.Detail.includes('categories') ||
            res.Detail.includes('parts'))
        ) {
          setShowLimitModal(true);
          setErrorMessage(res.Detail);
          enqueueSnackbar(res.Detail, {
            variant: 'error'
          });
        } else {
          enqueueSnackbar(res.Detail, {
            variant: 'error'
          });
        }
      } else {
        enqueueSnackbar('Order Placed!', {
          variant: 'success'
        });
        setFieldValue('notes', '');
        setFieldValue('descriptionId', '');
        setFieldValue('orderType', '');
        setFieldValue('chargeVendorId', '');
        setFieldValue('changeOrder', false);
        setFieldTouched('orderType', false);
        setFieldTouched('descriptionId', false);
        setFieldTouched('notes', false);
        setFieldValue('fileContents', '');
        setFieldValue('returnVendor', '');
        setFieldValue('photos', []);
        setFieldValue('photoGuids', []);
        setParts([]);
        fetchHistory(0, 10);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSaving(false);
    }
  };

  const checkVendors = () => {
    // Check if a vendor id exists for all parts except for undefined parts
    return parts
      .filter(part => !part.undefinedPartDescriptionId)
      .some(part => !part.vendorId === true);
  };
  const checkParts = () => {
    const undefinedParts = parts.filter(part => part.partName === UNDEFINED_PART);
    const invalidParts = undefinedParts.filter(
      part => !part.undefinedPartDescriptionId || (values.changeOrder && !part.pricePerUnit)
    );
    if (invalidParts.length > 0) {
      return true;
    }
    return false;
  };
  const checkImages = () => {
    // Check if there are images and prevent submission if required for specific instances.
    if (!values.photoGuids.length && isReturnPOOrderType) {
      return true;
    } else {
      return false;
    }
  };
  const isPlaceOrderButtonDisabled = () => {
    if (
      !isValid ||
      (values.changerOrder && !values.notes && !values.orderType) ||
      checkParts() ||
      checkVendors() ||
      checkImages()
    ) {
      return true;
    } else {
      return false;
    }
  };
  const updateMyPartsData = (part: IPart, columnId: string, value: string, rowIndex: number) => {
    // @ts-ignore
    setParts(old => {
      return old.map((row, oldPartIndex: number) => {
        if (oldPartIndex === rowIndex) {
          return {
            ...part,
            [columnId]: value
          };
        }
        return row;
      });
    });
  };

  useEffect(() => {
    if (values.orderType === 10 || values.orderType === 6) {
      setFieldValue('descriptionId', '100');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.orderType]);

  const [isLoadingUndefinedParts, setIsLoadingUndefinedParts] = useState<boolean>(false);
  const [undefinedParts, setUndefinedParts] = useState<IUndefinedPart[]>([]);
  const fetchUndefinedPartOptions = async (callback?: any) => {
    try {
      setIsLoadingUndefinedParts(true);
      const res = await getUndefinedParts({
        sortBy: 'description',
        sortDirection: 'Asc',
        showOnlyUsedInOrders: false
      });
      setUndefinedParts(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading undefined parts, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setIsLoadingUndefinedParts(false);
      if (callback) {
        callback();
      }
    }
  };

  const cartColumns = useMemo(
    () =>
      [
        {
          Header: 'Part',
          accessor: 'partName',
          overrideWidth: 500,
          sort: false,
          Cell: ({ cell: { row } }: { cell: { row } }) => {
            const part: IPart = row.original;
            return row.values.partName === UNDEFINED_PART ? (
              <PartNameRow
                id={part.id}
                part={part}
                handlePartNameChange={(undefinedPartDescriptionId: number) => {
                  const copy = [...parts];
                  copy[row.index] = {
                    ...copy[row.index],
                    undefinedPartDescriptionId
                  };
                  setParts(copy);
                }}
                isLoading={isLoadingUndefinedParts}
                undefinedParts={undefinedParts}
                updateOptions={callback => {
                  fetchUndefinedPartOptions(callback);
                }}
              />
            ) : (
              <Typography>{row.values.partName}</Typography>
            );
          }
        },
        {
          Header: 'Qty.',
          accessor: 'quantity',
          sort: false,
          overrideWidth: 150,
          Cell: props => {
            if (isReturnPOOrderType) {
              // Check to make sure the quantity isn't more than the ordered amount.
              // @ts-ignore
              const row = returnParts?.returnPOParts?.find((_, i: number) => i === props.row.index);
              return (
                <EditableCell
                  {...props}
                  isNumberOnly
                  props
                  endAdornment={props.cell.row.original.unit}
                  value={
                    props?.value > row?.quantityRemaining ? row?.quantityRemaining : props?.value
                  }
                />
              );
            } else {
              return (
                <EditableCell
                  {...props}
                  isNumberOnly
                  props
                  endAdornment={props.cell.row.original.unit}
                />
              );
            }
          }
        },
        // is a change order or a Return PO order type
        (values.changeOrder || isReturnPOOrderType) && {
          Header: 'Price',
          accessor: 'pricePerUnit',
          sort: false,
          overrideWidth: 125,
          Cell: ({ cell: { row } }: { cell: { row } }) => {
            const part: IPart & IReturnPOPart = row.original;
            if (part.partName === UNDEFINED_PART || !!part.undefinedPartDescriptionId) {
              return (
                <PriceRow
                  handlePriceChange={(pricePerUnit: number | undefined) => {
                    const copy = [...parts];
                    if (pricePerUnit) {
                      copy[row.index] = {
                        ...copy[row.index],
                        pricePerUnit: pricePerUnit
                      };
                    } else {
                      delete copy[row.index].pricePerUnit;
                    }
                    setParts(copy);
                  }}
                  isLoading={isLoadingUndefinedParts}
                  value={part.pricePerUnit}
                  isDisabled={isReturnPOOrderType}
                />
              );
            } else if (part.pricePerUnit) {
              return <>{formatMoney(part.pricePerUnit, 2)}</>;
            } else if (part.price) {
              return <>{formatMoney(part.price, 2)}</>;
            } else {
              return <>--</>;
            }
          }
        },
        {
          Header: 'Vendor',
          accessor: 'vendorName',
          sort: false,
          Cell: ({ cell: { row } }: { cell: { row } }) => {
            const part: IPart & IReturnPOPart = row.original;
            return row.values.partName !== UNDEFINED_PART ? (
              <VendorRow
                isRequired
                part={{
                  ...part,
                  partId: part.id,
                  quantity: part.quantity || 0,
                  purchaseOrderNumber: part.purchaseOrderNumber
                }}
                handleVendorChange={(vendorId: number, pricePerUnit: number) => {
                  const copy = [...parts];
                  copy[row.index] = {
                    ...copy[row.index],
                    vendorId,
                    pricePerUnit
                  };
                  setParts(copy);
                }}
              />
            ) : (
              <></>
            );
          }
        },
        {
          id: 'actions',
          accessor: '',
          Header: '',
          overrideWidth: 100,
          sort: false,
          Cell: ({ cell: { row } }: { cell: { row } }) => {
            return (
              <div className={classes.removeButtonWrapper}>
                <IconButton
                  color="default"
                  className={classes.deleteButton}
                  title="Remove part from cart"
                  onClick={() => {
                    setParts(parts.filter((_, partIndex) => partIndex !== row.index));
                  }}
                  size="large"
                >
                  <DeleteForever />
                </IconButton>
              </div>
            );
          }
        }
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.orderType, parts, undefinedParts, isLoadingUndefinedParts]
  );

  const [returnParts, setReturnParts] = useState<IReturnPartPO[]>([]);
  const [isLoadingReturnParts, setLoadingReturnParts] = useState<boolean>(false);
  const fetchReturnParts = async (passVendorId: string | number) => {
    setLoadingReturnParts(true);
    try {
      const res = await getReturnParts({
        vendorId: passVendorId,
        neighborhoodId: values.neighborhoodId,
        lotNumber: values.lotNumber
      });
      setReturnParts(res);
    } catch (error) {
      enqueueSnackbar(`Error loading parts, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setLoadingReturnParts(false);
    }
  };

  useEffect(() => {
    if (values.returnVendor) {
      fetchReturnParts(values.returnVendor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.returnVendor, values.returnVendor && showFindPartsModal]);
  return (
    <>
      {(isSaving || isLoadingFavoriteOrder) && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      <Prompt
        when={
          initialValues.changeOrder !== values.changeOrder ||
          initialValues.orderType !== values.orderType ||
          initialValues.notes !== values.notes ||
          parts.length > 0 ||
          initialValues.fileContents !== values.fileContents
        }
        message="Are you sure you want to navigate away from Place Order? Your unsaved order will be lost."
      />
      <Grid item xs={12} className={classes.wrapper}>
        <Typography variant="h6" gutterBottom>
          Order Detail
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <Field name={`orderType`}>
              {({ field }) => {
                return (
                  <SMBSelect
                    {...field}
                    variant="outlined"
                    label="Order Type"
                    required
                    size="small"
                    disabled={isLoadingTypes || isLoadingHistory}
                    value={values.orderType}
                    onBlur={handleBlur}
                    error={
                      errors && errors.orderType && touched && touched.orderType ? true : false
                    }
                    onChange={e => {
                      field.onChange(e);
                      // reset
                      setFieldValue('returnVendor', '');
                      // order type is changed from "PRODUCTION BACKCHARGE"/"WARRANTY BACKCHARGE" to a different type
                      // and it was orginally a "PRODUCTION BACKCHARGE" or "WARRANTY BACKCHARGE"
                      // then reset charge vendors to empty
                      if (
                        Number(e.target.value) !== 10 &&
                        Number(e.target.value) !== 6 &&
                        values?.chargeVendors?.length
                      ) {
                        setFieldValue('chargeVendors', []);
                      }
                      // if parts are in the cart and the user changes the order type to "Return PO" than reset the cart
                      if (parts?.length > 0 && Number(e.target.value) === 9) {
                        setParts([]);
                      }
                      handleOrderTypeChange(e.target.value as string);
                    }}
                    handleClear={() => {
                      setFieldValue('orderType', '');
                      handleOrderTypeChange('');
                    }}
                  >
                    {orderTypes &&
                      orderTypes
                        .filter(orderType =>
                          values.changeOrder
                            ? CHANGE_ORDER_TYPES[orderType.sageId]
                            : !CHANGE_ORDER_TYPES[orderType.sageId]
                        )
                        .map((type, index) => {
                          return (
                            <MenuItem key={`${index}`} value={type.sageId}>
                              {type.typeName}
                            </MenuItem>
                          );
                        })}
                  </SMBSelect>
                );
              }}
            </Field>
          </Grid>
          <Grid item xs={12} md={6}>
            <Field name={`changeOrder`}>
              {({ field }) => {
                return (
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={values.changeOrder}
                        id="active-inactive"
                        disabled={isLoadingTypes || isLoadingHistory}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFieldValue('changeOrder', e.target.checked);
                          setFieldValue('chargeVendors', []);
                          setFieldValue('orderType', '');
                          setFieldValue('descriptionId', '');
                        }}
                      />
                    }
                    label="Change Order"
                  />
                );
              }}
            </Field>
          </Grid>
          {/* RETURN PO order type */}
          {isReturnPOOrderType && (
            <Grid item xs={12} md={6}>
              <Field name={`returnVendor`}>
                {({ field }) => {
                  return (
                    <SMBSelect
                      {...field}
                      variant="outlined"
                      label={isLoadingVendors ? 'Loading...' : 'Select Return Vendor'}
                      required
                      size="small"
                      disabled={isLoadingVendors}
                      value={values.returnVendor}
                      onBlur={handleBlur}
                      onChange={field.onChange}
                      handleClear={() => {
                        setFieldValue('returnVendor', '');
                      }}
                    >
                      {availableVendors?.length === 0 && (
                        <MenuItem key={`not-found`} value={'not-found'}>
                          No Vendors Found
                        </MenuItem>
                      )}
                      {availableVendors &&
                        availableVendors.map((vendor, index) => {
                          return (
                            <MenuItem key={`${index}`} value={vendor.id}>
                              {vendor.name}
                            </MenuItem>
                          );
                        })}
                    </SMBSelect>
                  );
                }}
              </Field>
            </Grid>
          )}
          {(values.orderType === 6 || values.orderType === 10) && (
            <Grid item xs={12} md={6}>
              <Field name={`chargeVendors`}>
                {({ field }) => {
                  return (
                    <FormControl required fullWidth variant="outlined" size="small">
                      <Autocomplete
                        value={values.chargeVendors}
                        multiple
                        classes={{
                          paper: classes.paperBorder
                        }}
                        onChange={(event, newValue: INeighborhoodVendor[]) => {
                          if (newValue && newValue.length > 0) {
                            setFieldValue(`chargeVendors`, newValue);
                          } else {
                            setFieldValue(`chargeVendors`, []);
                          }
                        }}
                        id="chargeVendors"
                        options={availableVendors ?? []}
                        disabled={isLoadingVendors}
                        getOptionLabel={(option: INeighborhoodVendor) => {
                          // Regular option
                          return option.name;
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            error={
                              errors && errors.chargeVendors && touched && touched.chargeVendors
                                ? true
                                : false
                            }
                            name={`chargeVendors`}
                            autoComplete="none"
                            onBlur={handleBlur}
                            label="Charge Vendor"
                            variant="outlined"
                            size="small"
                          />
                        )}
                      />
                    </FormControl>
                  );
                }}
              </Field>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            md={isReturnPOOrderType || values.orderType === 6 || values.orderType === 10 ? 6 : 12}
          >
            <Field name={`descriptionId`}>
              {({ field }) => {
                return (
                  <SMBSelect
                    {...field}
                    variant="outlined"
                    label={isLoadingDescriptions ? 'Loading...' : 'Order Description'}
                    required
                    size="small"
                    disabled={
                      isLoadingDescriptions ||
                      isLoadingHistory ||
                      values.orderType === 10 ||
                      values.orderType === 6
                    }
                    value={values.descriptionId}
                    onBlur={handleBlur}
                    error={
                      errors && errors.descriptionId && touched && touched.descriptionId
                        ? true
                        : false
                    }
                    handleClear={() => {
                      setFieldValue('descriptionId', '');
                    }}
                  >
                    {descriptions &&
                      descriptions.map((description, index) => {
                        return (
                          <MenuItem key={`${index}`} value={description.id}>
                            {description.name}
                            {description.additionalDetails
                              ? ` - ${description.additionalDetails}`
                              : ''}
                          </MenuItem>
                        );
                      })}
                  </SMBSelect>
                );
              }}
            </Field>
          </Grid>
          <Grid item xs={12}>
            <Field name={`notes`}>
              {({ field }) => {
                return (
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    autoComplete="nope"
                    label="Notes"
                    name="notes"
                    multiline
                    minRows={3}
                    inputProps={{
                      style: {
                        resize: 'vertical'
                      }
                    }}
                    value={values.notes}
                    onBlur={handleBlur}
                    onChange={e => setFieldValue('notes', e.target.value)}
                    error={Boolean(touched.notes && errors.notes)}
                    helperText={touched.notes && errors.notes}
                  />
                );
              }}
            </Field>
          </Grid>
          <Grid item xs={12} md={6} className={classes.findPartWrapper}>
            <Tooltip
              title="Please select an Order Type"
              disableHoverListener={values.orderType ? true : false}
            >
              <span>
                <LoadingButton
                  type="button"
                  disabled={
                    !values.orderType ||
                    isLoadingTypes ||
                    (isReturnPOOrderType ? !values.returnVendor : false) ||
                    isLoadingReturnParts
                  }
                  color="secondary"
                  variant="contained"
                  loading={isLoadingReturnParts}
                  loadingPosition="start"
                  startIcon={<Search />}
                  onClick={() => setShowFindPartsModal(true)}
                >
                  {isReturnPOOrderType ? 'Select Part' : 'Find Part'}
                </LoadingButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
        <Typography variant="h6" className={classes.cartTitle}>
          Cart
        </Typography>
        <Table
          columns={cartColumns}
          data={parts}
          hidePagination
          noResultsText="No Parts in Cart"
          useTableProps={{
            updateMyData: updateMyPartsData
          }}
          ResponsiveComponent={isMobile ? MobileParts : undefined}
          mobileProps={{
            isModal: false,
            showPrice: values.changeOrder || isReturnPOOrderType,
            isLoadingUndefinedParts,
            isReturnPOOrderType,
            undefinedParts,
            handleDelete: (rowIndex: number) => {
              const result = window.confirm(
                'Are you sure you want to remove this part from your order?'
              );
              if (result) {
                setParts(parts.filter((_, index) => index !== rowIndex));
              }
            },
            handleQtyChange: (rowIndex: number, qty: string) => {
              const copy = JSON.parse(JSON.stringify(parts));
              copy[rowIndex] = {
                ...copy[rowIndex],
                quantity: Number(qty)
              };
              setParts(copy);
            },
            handleVendorChange: (rowIndex: number, vendorId: number, pricePerUnit: number) => {
              const copy = JSON.parse(JSON.stringify(parts));
              copy[rowIndex] = {
                ...copy[rowIndex],
                vendorId,
                pricePerUnit
              };
              setParts(copy);
            },
            handlePriceChange: (rowIndex: number, pricePerUnit: number | undefined) => {
              const copy = JSON.parse(JSON.stringify(parts));
              if (pricePerUnit) {
                copy[rowIndex] = {
                  ...copy[rowIndex],
                  pricePerUnit: pricePerUnit
                };
              } else {
                delete copy[rowIndex].pricePerUnit;
              }
              setParts(copy);
            },
            handlePartNameChange: (rowIndex: number, undefinedPartDescriptionId: number) => {
              const copy = JSON.parse(JSON.stringify(parts));
              copy[rowIndex] = {
                ...copy[rowIndex],
                undefinedPartDescriptionId
              };
              setParts(copy);
            },
            handleUndefinedPartsCallback: callback => {
              fetchUndefinedPartOptions(callback);
            }
          }}
        />
        {parts?.length === 0 && (
          <Box marginTop="8px">
            <Divider />
          </Box>
        )}
        <PlaceOrderImages
          setFieldValue={setFieldValue}
          photos={values.photos}
          photoGuids={values.photoGuids}
          isRequired={isReturnPOOrderType}
          setSelectedImage={setSelectedImage}
        />
        {parts && parts.length > 0 && (
          <SMButton
            id="place-order"
            className={classes.placeOrderButton}
            type="button"
            color="primary"
            variant="contained"
            disabled={isPlaceOrderButtonDisabled()}
            startIcon={<ShoppingCart />}
            onClick={() => {
              handleSubmit();
            }}
          >
            Place Order
          </SMButton>
        )}
      </Grid>
      <FindPartsModal
        open={showFindPartsModal}
        values={values}
        onClose={() => setShowFindPartsModal(false)}
        cartParts={parts}
        handlePartSelection={(part: any) => {
          const foundPartIndex = parts.findIndex(p => p.id === part.id);
          const foundPart = parts.find(p => p.id === part.id);
          // the part already exists in cart so then just add them together
          // ignore for "Return PO" order type orders
          if (foundPartIndex >= 0 && !isReturnPOOrderType) {
            // make a copy of the parts so we don't change the original
            const copy = JSON.parse(JSON.stringify(parts));
            copy[foundPartIndex] = {
              ...part,
              // add quantity together to get the total
              quantity: (Number(part.quantity) + Number(foundPart.quantity)) as unknown as string,
              vendorId: part?.vendorId ?? null
            };
            setParts(copy);
          } else {
            setParts([
              ...parts,
              {
                ...part,
                partName: part.partName,
                quantity: part.quantity as string,
                id: part.id,
                vendorId: part?.vendorId ?? null,
                undefinedPartDescriptionId: part?.undefinedPartDescriptionId ?? null,
                purchaseOrderNumber: values.orderType === 9 ? part?.purchaseOrderNumber : null
              }
            ]);
          }
          enqueueSnackbar('Part added to order!', {
            variant: 'success'
          });
          setFieldTouched('orderType', false);
        }}
        returnVendorId={values.returnVendor}
        isReturnOrder={isReturnPOOrderType}
        returnParts={returnParts}
        setReturnParts={setReturnParts}
      />
      <SpendLimitModal
        open={showLimitModal}
        handleClose={(val?: string) => {
          if (val && val === 'Yes') {
            handleSubmit(true);
          }
          setShowLimitModal(false);
        }}
        message={errorMessage}
      />
      <ImageModal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage('')}
        source={selectedImage}
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    padding: 16,
    paddingTop: `0 !important`
  },
  outlinedLabel: {
    backgroundColor: theme.palette.common.white,
    paddingLeft: 2,
    paddingRight: 2
  },
  cartTitle: {
    marginTop: theme.spacing(1)
  },
  findPartWrapper: {
    [theme.breakpoints.up('md')]: {
      marginTop: 8
    }
  },
  addCartWrapper: {
    [theme.breakpoints.up('md')]: {
      marginTop: 8
    }
  },
  placeOrderButton: {
    marginTop: theme.spacing(1)
  },
  deleteButton: {
    color: theme.palette.error.main
  },
  removeButtonWrapper: {
    textAlign: 'center'
  },
  paperBorder: {
    border: `1px solid ${theme.palette.grey[300]}`
  },
  undefinedPart: {
    color: theme.palette.error.main
  }
}));
